import type { KeybindingOverrideMap } from "~/helpers/keybindingOverrides";
import type { ChordBinding } from "~/keyboard/chordSequence";
import type { NavigationChord, NavigationChordId } from "~/keyboard/navigationChords";
import type { KeyboardScope } from "~/keyboard/scope";

import { effectiveBinding } from "~/helpers/keybindingOverrides";
import { isReservedBinding, parseBinding, toTinykeysBinding } from "~/keyboard/bindings";
import navigationChords, { NAVIGATION_CHORD_SCOPE } from "~/keyboard/navigationChords";
import moduleBrandingConfig from "~/layout/moduleBrandingConfig";
import { DEFAULT_MODULE_LOGO_SRC, resolveModuleBranding } from "~/layout/useModuleBranding";

export type GridRibbonActionId =
	| "grid.home.delete"
	| "grid.home.importExport"
	| "grid.home.new"
	| "grid.home.refresh"
	| "grid.view.filterBar"
	| "grid.view.formatting"
	| "grid.view.fullscreen"
	| "grid.view.layouts"
	| "grid.view.reset"
	| "grid.view.save"
	| "grid.view.selectedOnly"
	| "grid.view.share";

export type KeyboardActionId =
	| "dock.tab.activate"
	| "dock.tab.closeActive"
	| "dock.tab.next"
	| "dock.tab.previous"
	| "focusGlobalSearch"
	| "focusRail"
	| "focusRibbon"
	| "formActions.submit"
	| "help.close"
	| "help.showShortcuts"
	| "hintBadges.exit"
	| "hintBadges.toggle"
	| "modalDialog.submit"
	| "restock.orders.undoOverride"
	| "toggleAiPanel"
	| "toggleDetailPanel"
	| GridRibbonActionId
	| NavigationChordId;

export type KeyboardActionCategory = "Editing" | "Forms" | "Grid" | "Help" | "Navigation" | "Panels" | "Tabs";

export interface KeyboardAction {
	allowInInputs?: boolean;
	category: KeyboardActionCategory;
	defaultBinding: string | string[];
	editable: boolean;
	id: KeyboardActionId;
	label: string;
	scope: KeyboardScope;
	searchAliases?: string[];
}

const resolveNavigationChordLabel = (chord: NavigationChord): string =>
	resolveModuleBranding(chord.module, moduleBrandingConfig, {
		displayName: chord.label,
		logoSrc: DEFAULT_MODULE_LOGO_SRC
	}).displayName;

const navigationChordActions: KeyboardAction[] = navigationChords.map((chord) => ({
	category: "Navigation",
	defaultBinding: chord.binding,
	editable: true,
	id: chord.id,
	get label() {
		return `Go to ${resolveNavigationChordLabel(chord)}`;
	},
	scope: NAVIGATION_CHORD_SCOPE
}));

export interface GridRibbonKeytipSeed {
	id: GridRibbonActionId;
	label: string;
	letter: string;
}

export const gridRibbonKeytipSeeds: GridRibbonKeytipSeed[] = [
	{ id: "grid.view.fullscreen", label: "Toggle fullscreen grid", letter: "f" },
	{ id: "grid.view.selectedOnly", label: "Show selected rows only", letter: "o" },
	{ id: "grid.view.reset", label: "Reset grid view", letter: "r" },
	{ id: "grid.view.filterBar", label: "Toggle recent-filter bar", letter: "b" },
	{ id: "grid.view.formatting", label: "Open conditional formatting", letter: "c" },
	{ id: "grid.view.share", label: "Share grid layout", letter: "h" },
	{ id: "grid.view.layouts", label: "Open saved layouts", letter: "l" },
	{ id: "grid.view.save", label: "Save grid layout", letter: "s" },
	{ id: "grid.home.new", label: "New / add record", letter: "n" },
	{ id: "grid.home.importExport", label: "Import or export", letter: "i" },
	{ id: "grid.home.delete", label: "Delete record", letter: "d" },
	{ id: "grid.home.refresh", label: "Refresh grid data", letter: "e" }
];

export const gridRibbonKeytipLetter = (id: string): string | undefined =>
	gridRibbonKeytipSeeds.find((seed) => seed.id === id)?.letter;

const keyboardActions: KeyboardAction[] = [
	{
		category: "Navigation",
		defaultBinding: ["/", "mod+k"],
		editable: true,
		id: "focusGlobalSearch",
		label: "Focus search",
		scope: "global"
	},
	{
		category: "Navigation",
		defaultBinding: "mod+shift+f",
		editable: true,
		id: "focusRibbon",
		label: "Focus ribbon",
		scope: "global"
	},
	{
		category: "Navigation",
		defaultBinding: "mod+shift+m",
		editable: true,
		id: "focusRail",
		label: "Focus menu rail",
		scope: "global"
	},
	{
		category: "Help",
		defaultBinding: "Shift+?",
		editable: false,
		id: "help.showShortcuts",
		label: "Keyboard shortcuts",
		scope: "global"
	},
	{
		allowInInputs: true,
		category: "Help",
		defaultBinding: "Escape",
		editable: false,
		id: "help.close",
		label: "Close shortcuts overlay",
		scope: "modal"
	},
	...navigationChordActions,
	{
		category: "Panels",
		defaultBinding: "mod+.",
		editable: true,
		id: "toggleDetailPanel",
		label: "Toggle detail panel",
		scope: "global"
	},
	{
		category: "Panels",
		defaultBinding: "mod+j",
		editable: true,
		id: "toggleAiPanel",
		label: "Toggle AI assistant",
		scope: "global",
		searchAliases: ["fernie"]
	},
	{
		category: "Help",
		defaultBinding: "mod+/",
		editable: true,
		id: "hintBadges.toggle",
		label: "Show keyboard hints",
		scope: "global"
	},
	{
		category: "Help",
		defaultBinding: "Escape",
		editable: false,
		id: "hintBadges.exit",
		label: "Hide keyboard hints",
		scope: "global"
	},
	{
		allowInInputs: true,
		category: "Forms",
		defaultBinding: ["Enter", "mod+Enter"],
		editable: false,
		id: "modalDialog.submit",
		label: "Submit dialog",
		scope: "modal"
	},
	{
		allowInInputs: true,
		category: "Forms",
		defaultBinding: ["Enter", "mod+Enter"],
		editable: false,
		id: "formActions.submit",
		label: "Submit form",
		scope: "page"
	},
	{
		allowInInputs: false,
		category: "Editing",
		defaultBinding: "mod+z",
		editable: false,
		id: "restock.orders.undoOverride",
		label: "Undo quantity override",
		scope: "page"
	},
	{
		category: "Tabs",
		defaultBinding: ["Alt+1", "Alt+2", "Alt+3", "Alt+4", "Alt+5", "Alt+6", "Alt+7", "Alt+8", "Alt+9"],
		editable: false,
		id: "dock.tab.activate",
		label: "Activate tab by number",
		scope: "page"
	},
	{
		category: "Tabs",
		defaultBinding: "Alt+PageUp",
		editable: true,
		id: "dock.tab.previous",
		label: "Previous tab",
		scope: "page"
	},
	{
		category: "Tabs",
		defaultBinding: "Alt+PageDown",
		editable: true,
		id: "dock.tab.next",
		label: "Next tab",
		scope: "page"
	},
	{
		category: "Tabs",
		defaultBinding: "Alt+W",
		editable: true,
		id: "dock.tab.closeActive",
		label: "Close active tab",
		scope: "page"
	}
];

export const getKeyboardAction = (id: KeyboardActionId): KeyboardAction | undefined =>
	keyboardActions.find((action) => action.id === id);

export const bindingsForAction = (action: KeyboardAction): string[] =>
	Array.isArray(action.defaultBinding) ? action.defaultBinding : [action.defaultBinding];

export const effectiveBindingsForAction = (action: KeyboardAction, overrides: KeybindingOverrideMap = {}): string[] =>
	effectiveBinding(bindingsForAction(action), overrides[action.id]);

export const buildTinykeysBindingMap = (
	actions: KeyboardAction[] = keyboardActions,
	overrides: KeybindingOverrideMap = {}
): Map<string, KeyboardActionId[]> => {
	const bindingMap = new Map<string, KeyboardActionId[]>();
	for (const action of actions) {
		for (const binding of effectiveBindingsForAction(action, overrides)) {
			if (isReservedBinding(binding)) continue;
			if (parseBinding(binding).length > 1) continue;
			const tinykeysBinding = toTinykeysBinding(binding);
			const boundActions = bindingMap.get(tinykeysBinding) ?? [];
			boundActions.push(action.id);
			bindingMap.set(tinykeysBinding, boundActions);
		}
	}
	return bindingMap;
};

export const buildChordBindings = (
	actions: KeyboardAction[] = keyboardActions,
	overrides: KeybindingOverrideMap = {}
): ChordBinding[] => {
	const chordMap = new Map<string, ChordBinding>();
	for (const action of actions) {
		for (const binding of effectiveBindingsForAction(action, overrides)) {
			if (isReservedBinding(binding)) continue;
			const sequence = parseBinding(binding);
			if (sequence.length < 2) continue;
			const existing = chordMap.get(binding);
			if (existing) existing.actionIds.push(action.id);
			else chordMap.set(binding, { actionIds: [action.id], sequence });
		}
	}
	return [...chordMap.values()];
};

export default keyboardActions;
